import React, { useState, useEffect } from "react";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import { simplePostCall } from "../../api/ApiServices";
import ApiConfig from "../../api/ApiConfig";
import { toast, ToastContainer } from "react-toastify";
import logo from "../../images/logo.svg"
import "../../Styles/Feefrequency.css";
import "../../Styles/yellocalfcss/ReceiptLayout.css";

// Resizes an image file to fit within a 240x80 box before upload,
// keeping the aspect ratio. Images already smaller are left as-is.
function resizeImage(file) {
  const MAX_W = 240;
  const MAX_H = 80;
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(MAX_W / img.width, MAX_H / img.height, 1);
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        canvas.getContext("2d").drawImage(img, 0, 0, w, h);
        canvas.toBlob(
          (blob) => (blob ? resolve(blob) : reject(new Error("Resize failed."))),
          "image/png",
        );
      };
      img.onerror = () => reject(new Error("Could not load image."));
      img.src = reader.result;
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

// Uploads images picked via the CKEditor image button to the backend
// (resized to 30x30) and embeds the returned URL into the content.
function CKEditorUploadAdapter(editor) {
  editor.plugins.get("FileRepository").createUploadAdapter = (loader) => ({
    upload() {
      return loader.file
        .then((file) => resizeImage(file))
        .then(
          (blob) =>
            new Promise((resolve, reject) => {
              const data = new FormData();
              data.append("upload", blob, "image.png");
              fetch(ApiConfig.ckeditorupload, {
                method: "POST",
                headers: {
                  customer_id: localStorage.getItem("customer_id"),
                  api_key: localStorage.getItem("api_key"),
                },
                body: data,
              })
                .then((res) => res.json())
                .then((json) => {
                  if (json && json.url) {
                    resolve({ default: json.url });
                  } else {
                    reject(json?.error?.message || "Image upload failed.");
                  }
                })
                .catch(() => reject("Image upload failed."));
            }),
        );
    },
    abort() {},
  });
}

const editorConfig = { extraPlugins: [CKEditorUploadAdapter] };

export default function ReceiptLayoutSettings() {
  const [header, setHeader] = useState("");
  const [footer, setFooter] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const schoolName = "Demo School For Development";

  // Fetch header/footer from backend
  useEffect(() => {
    getReceiptSettings();
  }, []);

  const getReceiptSettings = async () => {
    setIsLoading(true);
    const customer_id = localStorage.getItem("customer_id");
    const api_key = localStorage.getItem("api_key");

    const requestBody = {
      customer_id: Number(customer_id),
      api_key,
      language: "en",
    };

    try {
      const response = await simplePostCall(
        ApiConfig.receiptlayout,
        JSON.stringify(requestBody),
      );
      if (response?.result && response?.invoice_layout?.length > 0) {
        const data = response.invoice_layout[0];
        setHeader(data.invoice_header || "");
        setFooter(data.invoice_footer || "");
      }
    } catch (error) {
      console.error("Error fetching receipt settings:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Save layout
  const handleSave = async () => {
    if (!header.trim() || !footer.trim()) {
      toast.warning("Please enter Header and Footer first!");
      return;
    }

    setIsLoading(true);
    const customer_id = localStorage.getItem("customer_id");
    const api_key = localStorage.getItem("api_key");
    const user_id = localStorage.getItem("user_id");

    const requestBody = {
      customer_id: Number(customer_id),
      api_key,
      user_id: Number(user_id),
      language: "en",
      ReceiptLayout: [
        {
          receipt_layout_header: header,
          receipt_layout_footer: footer,
        },
      ],
    };

    try {
      const response = await simplePostCall(
        ApiConfig.receiptlayout,
        JSON.stringify(requestBody),
      );
      if (response?.result) {
        toast.success("Receipt Layout Saved Successfully!", {
          autoClose: 1500,
        });
        setShowPreview(true);
      } else {
        toast.warning(response?.message || "Failed to save layout.");
      }
    } catch (error) {
      console.error("Error saving receipt layout:", error);
      toast.error("An error occurred while saving.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="receiplayout-container">
      <ToastContainer />

      <div className="special-header panel-white-all">
        <h4 className="page-title">Receipt Layout Settings</h4>
      </div>

      <div className="rl-page-body">
        <div className="rl-grid">
          {/* ================= LEFT SIDE ================= */}
          <div className="rl-editor-card">
            <div className="editor-section">
              <label>Enter Receipt Header</label>
              <CKEditor
                editor={ClassicEditor}
                config={editorConfig}
                data={header}
                onChange={(event, editor) => setHeader(editor.getData())}
              />
            </div>

            <div className="editor-section">
              <label>Enter Receipt Footer</label>
              <CKEditor
                editor={ClassicEditor}
                config={editorConfig}
                data={footer}
                onChange={(event, editor) => setFooter(editor.getData())}
              />
            </div>

            <button
              className="btn btn-primary rl-save-btn"
              onClick={handleSave}
              disabled={isLoading}
            >
              {isLoading ? "Generating PDF..." : "Save & Generate PDF Preview"}
            </button>
          </div>

          {/* ================= RIGHT SIDE ================= */}
          <div className="rl-preview-card">
            <h5 className="rl-preview-title">PDF Receipt Preview</h5>
            {showPreview ? (
              <div className="rl-preview-area">
                <div className="rl-a4">
                  <div className="rl-a4-logo">
                    <img src={logo} alt="logo" />
                    <div className="rl-a4-school">{schoolName}</div>
                  </div>

                  <div
                    className="rl-a4-section"
                    dangerouslySetInnerHTML={{ __html: header || "Header Content" }}
                  />

                  <div className="rl-a4-title">
                    Monthly Addon Subscription Invoice for February-2026
                  </div>

                  <table className="rl-a4-table">
                    <thead>
                      <tr>
                        <th>Sl.No.</th>
                        <th>Addon Name</th>
                        <th>Addon Type</th>
                        <th>Subscription Price ($)</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr><td>1</td><td>Time Table</td><td>Web</td><td>10</td></tr>
                      <tr><td>2</td><td>Driver Console</td><td>Mobile</td><td>10</td></tr>
                      <tr><td>3</td><td>Teacher App</td><td>Mobile</td><td>10</td></tr>
                      <tr><td>4</td><td>Parent App</td><td>Mobile</td><td>10</td></tr>

                      <tr className="rl-a4-total">
                        <td colSpan="3">Total Amount ($)</td>
                        <td>40</td>
                      </tr>
                    </tbody>
                  </table>

                  <div
                    className="rl-a4-footer"
                    dangerouslySetInnerHTML={{ __html: footer || "Footer Content" }}
                  />
                </div>
              </div>
            ) : (
              <div className="rl-preview-empty">
                <i className="bi bi-file-pdf"></i>
                <p>Save changes to generate and Preview PDF</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
